import { collection, getDocsFromServer, getFirestore, query } from 'firebase/firestore';
import { firebaseApp } from '../utilities/firebase';
import { coordinateForField, distanceInMeters } from './geo';

const displayableAddress = (field) =>
    field?.displayable_address ?? field?.address?.displayable_address;

const filterForSearch = (fieldSnapshot, searchTerm) => {
    const field = fieldSnapshot.data;
    if (field === undefined || field?.group_name === undefined || field?.address?.displayable_address === undefined) {
      return false;
    }

    if (field.deleted === true) {
      return false;
    }

    const trimmedSearchTerm = searchTerm.trim().toUpperCase();
    return field.group_name.toUpperCase().includes(trimmedSearchTerm) ||
           field.address.displayable_address.toUpperCase().includes(trimmedSearchTerm)
  }

  const groupedByAddress = (docs) => {
    let addresses = []
    return docs.filter(d => {
      // Fields without any address are kept individually (grouping them
      // under `undefined` would collapse unrelated fields into one result).
      const address = displayableAddress(d.data);
      if (address === undefined) {
        return true;
      }

      let unique = true;
      if (addresses.includes(address)) {
        unique = false;
      }
      addresses.push(address)
      return unique;
    })
  }

  const matchingFilteredGroupedDocs = (docs, searchTerm) => {
    return groupedByAddress(docs.filter(fDoc => filterForSearch(fDoc, searchTerm)));
  }

/**
 * Loads every field document as `{id, data}`, via the 24h localStorage cache
 * (see CacheReset.js) or a full server read. Shared by search and the map.
 */
export async function loadAllFieldDocs() {
    const jsonInStorage = localStorage.getItem('ffb-fields')

    if (jsonInStorage) {
      return JSON.parse(jsonInStorage);
    }

    const db = getFirestore(firebaseApp);
    const collectionRef = collection(db, "fields")
    const q = query(collectionRef);

    // Always read the full collection from the server here. The SDK cache
    // only holds whatever documents earlier pages happened to load, so a
    // cache read after visiting a field page returns a tiny partial list —
    // and that partial list would then be persisted to localStorage as if
    // it were the whole database. localStorage (with the 24h reset in
    // CacheReset.js) is the caching layer.
    const querySnapshot = await getDocsFromServer(q);

    const convertedSnapshotDocs = querySnapshot.docs.map(d => {
      return {id: d.id, data: d.data()}
    })

    try {
      localStorage.setItem('ffb-fields', JSON.stringify(convertedSnapshotDocs));
    } catch {
      // Storage quota exceeded — proceed without the cache.
    }

    return convertedSnapshotDocs;
}

/** Every non-deleted field group (one doc per address) with coordinates. */
export async function loadFieldGroupsForMap() {
    const docs = await loadAllFieldDocs();
    return groupedByAddress(
      docs.filter(d => d.data && d.data.deleted !== true),
    ).filter(d => coordinateForField(d.data) !== undefined);
}

export async function getFields(searchTerm, setFieldsSnapshot, setFieldsError, setFieldsLoading, options = {}) {
    try {
      const docs = await loadAllFieldDocs();
      let fields = matchingFilteredGroupedDocs(docs, searchTerm);

      // When the query resolved to a place (zip code, city), rank matches by
      // distance to it; with no name matches at all, show the closest fields
      // to that place instead of an empty page.
      const near = options.nearCoordinate;
      if (near) {
        if (fields.length === 0) {
          fields = groupedByAddress(
            docs.filter(d => d.data && d.data.deleted !== true),
          ).filter(d => coordinateForField(d.data) !== undefined);
        }

        fields = [...fields].sort((a, b) => {
          const aCoordinate = coordinateForField(a.data);
          const bCoordinate = coordinateForField(b.data);
          const aDistance = aCoordinate ? distanceInMeters(near, aCoordinate) : Number.POSITIVE_INFINITY;
          const bDistance = bCoordinate ? distanceInMeters(near, bCoordinate) : Number.POSITIVE_INFINITY;
          return aDistance - bDistance;
        }).slice(0, options.nearLimit ?? 25);
      }

      setFieldsSnapshot({docs: fields});
      setFieldsError(undefined);
    } catch (error) {
      console.error(error);
      setFieldsError(error);
    }

    setFieldsLoading(false);
}
